import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLessons } from "../contexts/LessonsContext";
import PrivateLessonsHeader from "../components/ui/PrivateLessonsHeader";
import ExpandableSection from "../components/ui/ExpandableSection";

const LessonRequestItem = ({ lesson, onAction, loading, toLocalDate }) => {
  const now = new Date();

  const paymentDeadline = lesson?.paymentDeadlineAt
    ? new Date(lesson.paymentDeadlineAt)
    : null;

  const lessonStart = toLocalDate(lesson.date, lesson.startTime);

  const isPaid = lesson.status === "PAID" || !!lesson.purchased;

  const isExpired =
    !!lesson.paymentExpired ||
    (lesson.status === "APPROVED" &&
      paymentDeadline &&
      !Number.isNaN(paymentDeadline.getTime()) &&
      paymentDeadline.getTime() <= now.getTime());

  const oneHourBeforeStartLocked =
    lessonStart &&
    !Number.isNaN(lessonStart.getTime()) &&
    lessonStart.getTime() <= now.getTime() + 60 * 60 * 1000;

  const isLocked =
    isPaid ||
    isExpired ||
    lesson.status === "CANCELLED" ||
    oneHourBeforeStartLocked;

  const canApprove =
    !isLocked &&
    ["NO_RESPONSE", "NOT_APPROVED"].includes(lesson.status);

  const canReject =
    !isLocked &&
    ["NO_RESPONSE", "APPROVED"].includes(lesson.status);

  const getStatusBadge = () => {
    const base = "flex-shrink-0 px-2 py-0.5 text-[10px] sm:text-xs rounded-full border font-medium";

    if (isPaid) {
      return <span className={`${base} bg-blue-100 text-blue-700 border-blue-200`}>שולם</span>;
    }

    if (isExpired) {
      return <span className={`${base} bg-rose-100 text-rose-700 border-rose-200`}>פג תוקף</span>;
    }

    const statusConfig = {
      NO_RESPONSE:  { className: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "ממתין לאישור" },
      APPROVED:     { className: "bg-green-100 text-green-700 border-green-200", label: "מאושר" },
      NOT_APPROVED: { className: "bg-red-100 text-red-700 border-red-200", label: "נדחה" },
    };

    const config = statusConfig[lesson.status] || statusConfig.NO_RESPONSE;
    return <span className={`${base} ${config.className}`}>{config.label}</span>;
  };

  const formatDate = (date) => {
    const d = toLocalDate(date);
    return d ? d.toLocaleDateString("he-IL") : "תאריך לא תקין";
  };

  return (
    <div className="group relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="absolute top-2 left-2 z-20 flex gap-1">
        {canReject && (
          <button
            onClick={() => onAction(lesson.id, "reject")}
            disabled={!!loading}
            title="דחה בקשה"
            className="w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 border border-red-100 hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            {loading === "reject" ? (
              <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        )}

        {canApprove && (
          <button
            onClick={() => onAction(lesson.id, "approve")}
            disabled={!!loading}
            title="אשר בקשה"
            className="w-6 h-6 flex items-center justify-center rounded-full bg-green-50 text-green-500 border border-green-100 hover:bg-green-100 transition-colors disabled:opacity-50"
          >
            {loading === "approve" ? (
              <div className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        )}
      </div>

      <div className="flex min-w-0">
        <div className="w-20 sm:w-28 flex-shrink-0 bg-indigo-50 border-l border-indigo-100 flex items-center justify-center self-stretch">
          <span className="text-indigo-300 text-xs">שיעור</span>
        </div>

        <div className="flex-1 min-w-0 p-2 sm:p-3 flex flex-col justify-between gap-1 min-h-20 sm:min-h-28">
          <h3 className="text-xs sm:text-sm font-bold text-gray-900 truncate leading-snug">
            {lesson.title}
          </h3>

          <span className="flex items-center gap-1 text-xs text-gray-500">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>{formatDate(lesson.date)}</span>
          </span>

          <div className="flex items-center gap-2 sm:gap-10 min-w-0">
            <span className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span dir="ltr" className="tabular-nums">
                {lesson.startTime} – {lesson.endTime}
              </span>
            </span>

            {getStatusBadge()}

            <span className="font-bold text-blue-800 whitespace-nowrap tabular-nums text-sm mr-auto flex-shrink-0">
              ₪{lesson.price}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivateLessonsManagement = () => {
  const {
    loading: lessonsLoading,
    fetchPrivateRequests,
    approvePrivateRequest,
    rejectPrivateRequest,
    _internals: { toLocalDate },
  } = useLessons();

  const [requests, setRequests] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [filter, setFilter] = useState("all");

  const refreshRequests = useCallback(async () => {
    try {
      const items = await fetchPrivateRequests();
      setRequests(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error("[PrivateLessons] refresh failed:", e);
      setRequests([]);
    }
  }, [fetchPrivateRequests]);

  useEffect(() => {
    let isActive = true;

    const refresh = async () => {
      if (!isActive) return;
      await refreshRequests();
    };

    refresh();

    const onFocus = () => {
      if (!isActive) return;
      refresh();
    };

    const onVisibility = () => {
      if (!isActive) return;
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      isActive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshRequests]);

  const getComputedFlags = useCallback((lesson) => {
    const now = new Date();
    const paymentDeadline = lesson?.paymentDeadlineAt
      ? new Date(lesson.paymentDeadlineAt)
      : null;
    const lessonStart = toLocalDate(lesson.date, lesson.startTime);

    const isPaid = lesson.status === "PAID" || !!lesson.purchased;

    const isExpired =
      !!lesson.paymentExpired ||
      (lesson.status === "APPROVED" &&
        paymentDeadline &&
        !Number.isNaN(paymentDeadline.getTime()) &&
        paymentDeadline.getTime() <= now.getTime());

    const oneHourBeforeStartLocked =
      lessonStart &&
      !Number.isNaN(lessonStart.getTime()) &&
      lessonStart.getTime() <= now.getTime() + 60 * 60 * 1000;

    return { isPaid, isExpired, oneHourBeforeStartLocked };
  }, [toLocalDate]);

  const filteredLessons = useMemo(() => {
    return requests
      .filter((lesson) => {
        const { isPaid, isExpired } = getComputedFlags(lesson);

        if (isPaid) return false;

        if (filter === "pending") return lesson.status === "NO_RESPONSE";
        if (filter === "approved") return lesson.status === "APPROVED" && !isExpired;
        if (filter === "rejected") return lesson.status === "NOT_APPROVED";
        if (filter === "expired") return isExpired;

        return true;
      })
      .sort((a, b) => {
        const aTime = toLocalDate(a.date, a.startTime);
        const bTime = toLocalDate(b.date, b.startTime);

        const aTs = aTime && !Number.isNaN(aTime.getTime()) ? aTime.getTime() : Number.MAX_SAFE_INTEGER;
        const bTs = bTime && !Number.isNaN(bTime.getTime()) ? bTime.getTime() : Number.MAX_SAFE_INTEGER;

        return aTs - bTs;
      });
  }, [requests, filter, getComputedFlags, toLocalDate]);

  const pendingCount = useMemo(
    () => requests.filter((l) => l.status === "NO_RESPONSE" && !(l.status === "PAID" || l.purchased)).length,
    [requests]
  );

  const approvedCount = useMemo(
    () =>
      requests.filter((l) => {
        const { isPaid, isExpired } = getComputedFlags(l);
        return !isPaid && l.status === "APPROVED" && !isExpired;
      }).length,
    [requests, getComputedFlags]
  );

  const expiredCount = useMemo(
    () => requests.filter((l) => getComputedFlags(l).isExpired).length,
    [requests, getComputedFlags]
  );

  const totalCount = useMemo(
    () => requests.filter((l) => !(l.status === "PAID" || l.purchased)).length,
    [requests]
  );

  const handleAction = async (lessonId, action) => {
    setActionLoading((prev) => ({ ...prev, [lessonId]: action }));
    try {
      if (action === "approve") {
        await approvePrivateRequest(lessonId);
      } else if (action === "reject") {
        await rejectPrivateRequest(lessonId);
      }

      await refreshRequests();
    } catch (error) {
      console.error("Error performing action:", error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [lessonId]: null }));
    }
  };

  return (
    <div className="md:min-h-screen bg-gray-100">
      <PrivateLessonsHeader />

      <main className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 pt-4 pb-10 sm:pb-0" dir="rtl">
        <div className="mb-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-xl border border-yellow-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-yellow-700">ממתינים</p>
                  <p className="text-2xl font-bold text-yellow-900">{pendingCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-green-700">אושרו</p>
                  <p className="text-2xl font-bold text-green-900">{approvedCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-xl border border-rose-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-rose-700">פג תוקף</p>
                  <p className="text-2xl font-bold text-rose-900">{expiredCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-blue-700">סה"כ בקשות</p>
                  <p className="text-2xl font-bold text-blue-900">{totalCount}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit max-w-full overflow-x-auto">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                filter === "all" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              הכל ({totalCount})
            </button>
            <button
              onClick={() => setFilter("pending")}
              className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                filter === "pending" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              ממתינים ({pendingCount})
            </button>
            <button
              onClick={() => setFilter("approved")}
              className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                filter === "approved" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              אושרו ({approvedCount})
            </button>
            <button
              onClick={() => setFilter("expired")}
              className={`px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                filter === "expired" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              }`}
            >
              פג תוקף ({expiredCount})
            </button>
          </div>
        </div>

        {lessonsLoading && <div className="mb-3 text-sm text-gray-500">טוען נתונים…</div>}

        <div className="space-y-4">
          {filteredLessons.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">אין בקשות לשיעורים פרטיים</h3>
              <p className="text-gray-600">כאשר יהיו בקשות חדשות, הן יופיעו כאן</p>
            </div>
          ) : (
            <div className="max-w-4xl">
              <ExpandableSection
                title={`בקשות לשיעורים פרטיים (${filteredLessons.length})`}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                }
                headerClassName="bg-gradient-to-l from-indigo-50 to-blue-50"
              >
                <div className="px-4 pt-3 pb-4 sm:px-5 sm:pt-4 sm:pb-5 space-y-2.5 sm:space-y-3" dir="rtl">
                  {filteredLessons.map((lesson) => (
                    <LessonRequestItem
                      key={lesson.id}
                      lesson={lesson}
                      onAction={handleAction}
                      loading={actionLoading[lesson.id]}
                      toLocalDate={toLocalDate}
                    />
                  ))}
                </div>
              </ExpandableSection>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PrivateLessonsManagement;